import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PopupProvider } from 'context/PopupProvider';
import { AuthProvider, useAuth } from 'context/AuthProvider';
import { ConsumerAuthProvider } from 'context/ConsumerAuthProvider';
import { ThemeProvider } from 'context/ThemeProvider';
import Layout from 'components/Layout';
import ConsumerLayout from 'components/ConsumerLayout';
import { RequireConsumerAuth, RequireConsumerPermission } from 'components/RequireConsumerAuth';
import Login from 'pages/Login';
import ConsumerLogin from 'pages/consumer/ConsumerLogin';
import ConsumerDashboard from 'pages/consumer/ConsumerDashboard';
import ConsumerGames from 'pages/consumer/ConsumerGames';
import ConsumerUsers from 'pages/consumer/ConsumerUsers';
import ConsumerBattles from 'pages/consumer/ConsumerBattles';
import ConsumerTransactions from 'pages/consumer/ConsumerTransactions';
import ConsumerPrivateTables from 'pages/consumer/ConsumerPrivateTables';
import Dashboard from 'pages/Dashboard';
import GameManagement from 'pages/GameManagement';
import ConsumerManagement from 'pages/ConsumerManagement';
import ConsumerDetail from 'pages/ConsumerDetail';
import VariantManagement from 'pages/VariantManagement';
import TableManagement from 'pages/TableManagement';
import GameConfiguration from 'pages/GameConfiguration';
import PlayerManagement from 'pages/PlayerManagement';
import PlayerEnforcement from 'pages/PlayerEnforcement';
import WalletMonitoring from 'pages/WalletMonitoring';
import GameMonitoring from 'pages/GameMonitoring';
import BattleDetail from 'pages/BattleDetail';
import PrivateTableManagement from 'pages/PrivateTableManagement';
import PrivateTableDetail from 'pages/PrivateTableDetail';
import RolePermission from 'pages/RolePermission';
import IntegrationLogs from 'pages/IntegrationLogs';
import IntegrationLogDetail from 'pages/IntegrationLogDetail';

const RequireAuth = ({ children }: { children: React.ReactElement }) => {
  const { token } = useAuth();
  const location = useLocation();
  if (!token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
};

// Role & Permission management is restricted to Super Admins. Any other admin
// who navigates directly to /roles is bounced back to the dashboard.
const RequireSuperAdmin = ({ children }: { children: React.ReactElement }) => {
  const { admin } = useAuth();
  if (!admin?.isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

/** Remount list on each navigation so returning from battle detail always refetches. */
const BattlesListPage = () => {
  const { key } = useLocation();
  return <GameMonitoring key={key} />;
};

function DocumentTitle() {
  const { pathname } = useLocation();

  useEffect(() => {
    document.title = pathname.startsWith('/consumer') ? 'Consumer Panel' : 'Admin Panel';
  }, [pathname]);

  return null;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ConsumerAuthProvider>
        <PopupProvider>
        <Router>
          <DocumentTitle />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/consumer/login" element={<ConsumerLogin />} />
            <Route
              path="/consumer"
              element={
                <RequireConsumerAuth>
                  <ConsumerLayout />
                </RequireConsumerAuth>
              }
            >
              <Route index element={<Navigate to="/consumer/dashboard" replace />} />
              <Route
                path="dashboard"
                element={
                  <RequireConsumerPermission moduleKey="dashboard">
                    <ConsumerDashboard />
                  </RequireConsumerPermission>
                }
              />
              <Route
                path="games"
                element={
                  <RequireConsumerPermission moduleKey="games">
                    <ConsumerGames />
                  </RequireConsumerPermission>
                }
              />
              <Route
                path="users"
                element={
                  <RequireConsumerPermission moduleKey="users">
                    <ConsumerUsers />
                  </RequireConsumerPermission>
                }
              />
              <Route
                path="battles"
                element={
                  <RequireConsumerPermission moduleKey="battles">
                    <ConsumerBattles />
                  </RequireConsumerPermission>
                }
              />
              <Route
                path="users/:id"
                element={
                  <RequireConsumerPermission moduleKey="users">
                    <PlayerEnforcement />
                  </RequireConsumerPermission>
                }
              />
              <Route
                path="battles/:id"
                element={
                  <RequireConsumerPermission moduleKey="battles">
                    <BattleDetail />
                  </RequireConsumerPermission>
                }
              />
              <Route
                path="transactions"
                element={
                  <RequireConsumerPermission moduleKey="transactions">
                    <ConsumerTransactions />
                  </RequireConsumerPermission>
                }
              />
              <Route
                path="private-tables"
                element={
                  <RequireConsumerPermission moduleKey="private-tables">
                    <ConsumerPrivateTables />
                  </RequireConsumerPermission>
                }
              />
            </Route>
            <Route
              path="/"
              element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="games" element={<GameManagement />} />
              <Route path="consumers" element={<ConsumerManagement />} />
              <Route path="consumers/:id" element={<ConsumerDetail />} />
              <Route path="variants" element={<VariantManagement />} />
              <Route path="tables" element={<TableManagement />} />
              <Route path="config" element={<GameConfiguration />} />
              <Route path="players" element={<PlayerManagement />} />
              <Route path="players/:id/enforcement" element={<PlayerEnforcement />} />
              <Route path="wallet" element={<WalletMonitoring />} />
              <Route path="battles" element={<BattlesListPage />} />
              <Route path="game-ledger" element={<Navigate to="/battles" replace />} />
              <Route path="battles/:id" element={<BattleDetail />} />
              <Route path="private-tables" element={<PrivateTableManagement />} />
              <Route path="private-tables/:id" element={<PrivateTableDetail />} />
              <Route
                path="roles"
                element={
                  <RequireSuperAdmin>
                    <RolePermission />
                  </RequireSuperAdmin>
                }
              />
              <Route
                path="integration-logs"
                element={
                  <RequireSuperAdmin>
                    <IntegrationLogs />
                  </RequireSuperAdmin>
                }
              />
              <Route
                path="integration-logs/:retryGroupId"
                element={
                  <RequireSuperAdmin>
                    <IntegrationLogDetail />
                  </RequireSuperAdmin>
                }
              />
            </Route>
          </Routes>
        </Router>
        </PopupProvider>
        </ConsumerAuthProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
